import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Plus, Wifi, WifiOff, AlertCircle } from 'lucide-react';
import { PageHeader, PageBody } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { Tooltip } from '@/components/ui/Tooltip';
import { Modal } from '@/components/ui/Modal';
import { toast } from '@/components/ui/Toaster';
import { OperatorPicker } from '@/components/ui/OperatorPicker';
import { useTenants, useCreateTenant } from '@/api/tenants';
import { usePlans } from '@/api/plans';
import { TENANT_STATUS_LABEL, TENANT_STATUS_TONE, WORKSPACE_STATUS_TONE } from '@/lib/tier';
import { relativeTime } from '@/lib/format';

export function TenantsRoute() {
  const [q, setQ] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const { data: apiTenants = [], isLoading } = useTenants();
  const isLive = apiTenants.length > 0;

  const filtered = useMemo(() => {
    if (!q) return apiTenants;
    const ql = q.toLowerCase();
    return apiTenants.filter(t =>
      `${t.name} ${t.slug} ${t.owner_name} ${t.owner_email}`.toLowerCase().includes(ql),
    );
  }, [q, apiTenants]);

  const workspaceCount = apiTenants.reduce((a, t) => a + t.workspace_count, 0);

  return (
    <>
      <PageHeader
        eyebrow="Platform"
        title="Customers"
        description={
          isLoading
            ? 'Loading…'
            : apiTenants.length === 0
              ? 'Backend not connected — start the admin-api to see customers.'
              : `${apiTenants.length} customer${apiTenants.length === 1 ? '' : 's'} · ${workspaceCount} instance${workspaceCount === 1 ? '' : 's'} under management.`
        }
        actions={
          <>
            <Tooltip
              maxWidth={240}
              content={
                <div>
                  <div className="font-semibold mb-0.5">
                    {isLive ? 'Connected' : isLoading ? 'Connecting…' : 'Backend unreachable'}
                  </div>
                  <div className="text-[11px] opacity-90">
                    {isLive ? 'Live from /admin/v1/tenants.' : 'Start admin-api in backend/ to see data.'}
                  </div>
                </div>
              }
            >
              <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-2xs font-semibold uppercase tracking-wider border">
                {isLive ? (
                  <>
                    <Wifi size={11} className="text-[color:var(--success)]" />
                    <span className="text-[color:var(--success)]">Live</span>
                  </>
                ) : (
                  <>
                    <WifiOff size={11} className="text-fg-subtle" />
                    <span className="text-fg-subtle">{isLoading ? '…' : 'Down'}</span>
                  </>
                )}
              </span>
            </Tooltip>
            <Button variant="primary" size="sm" iconLeft={<Plus size={13} />} onClick={() => setAddOpen(true)}>
              Onboard customer
            </Button>
          </>
        }
      />
      <PageBody>
        <Card>
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border">
            <div className="relative flex-1 max-w-sm">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search name, slug, owner…"
                className="w-full h-7 pl-7 pr-2 rounded-md bg-surface-2 border border-transparent text-sm hover:border-border focus:border-accent focus:outline-none"
              />
            </div>
            <div className="ml-auto text-2xs text-fg-subtle">
              {filtered.length} of {apiTenants.length}
            </div>
          </div>

          {apiTenants.length === 0 && !isLoading ? (
            <div className="px-6 py-12 text-center">
              <div className="text-md font-semibold text-fg mb-1">No customers connected</div>
              <p className="text-sm text-fg-muted max-w-md mx-auto">
                Start the admin-api backend in <code className="font-mono text-xs">backend/</code>, then this list will populate from <code className="font-mono text-xs">tenants.yaml</code>.
              </p>
            </div>
          ) : (
            <Table>
              <THead>
                <tr>
                  <TH>Customer</TH>
                  <TH>Owner</TH>
                  <TH className="text-right">Workspaces</TH>
                  <TH>Health</TH>
                  <TH>Status</TH>
                  <TH className="whitespace-nowrap">Onboarded</TH>
                </tr>
              </THead>
              <TBody>
                {filtered.map(t => (
                  <TR key={t.id}>
                    <TD>
                      <Link to={`/tenants/${t.slug}`} className="block group">
                        <div className="font-semibold text-fg group-hover:text-accent">{t.name}</div>
                        <div className="text-2xs text-fg-subtle font-mono">{t.slug}</div>
                      </Link>
                    </TD>
                    <TD>
                      <div className="text-sm text-fg">{t.owner_name}</div>
                      <div className="text-2xs text-fg-subtle">{t.owner_email}</div>
                    </TD>
                    <TD className="text-right tabnum">{t.workspace_count}</TD>
                    <TD>
                      <Badge tone={WORKSPACE_STATUS_TONE[t.worst_workspace_status]}>
                        {t.worst_workspace_status}
                      </Badge>
                    </TD>
                    <TD>
                      <Badge tone={TENANT_STATUS_TONE[t.status]}>{TENANT_STATUS_LABEL[t.status]}</Badge>
                    </TD>
                    <TD className="text-fg-muted text-xs">{relativeTime(t.created_at)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}

          {filtered.length === 0 && apiTenants.length > 0 && (
            <div className="px-4 py-8 text-center text-sm text-fg-muted">
              No customers match "{q}".
            </div>
          )}
        </Card>
      </PageBody>

      <AddCustomerModal open={addOpen} onClose={() => setAddOpen(false)} />
    </>
  );
}

function AddCustomerModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const nav = useNavigate();
  const create = useCreateTenant();
  const plans = usePlans();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  // Auto-derive slug from name UNTIL the user types into the slug field.
  // Once touched, the slug stays under the user's control.
  const [slugTouched, setSlugTouched] = useState(false);
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [supportOwner, setSupportOwner] = useState('');
  const [tier, setTier] = useState('business');
  const [notes, setNotes] = useState('');

  const validSlug = /^[a-z0-9][a-z0-9-]{1,40}[a-z0-9]$/.test(slug.trim());
  const validEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(ownerEmail.trim());
  const canSubmit =
    name.trim().length > 0 &&
    validSlug &&
    ownerName.trim().length > 0 &&
    validEmail &&
    !create.isPending;

  function reset() {
    setName(''); setSlug(''); setSlugTouched(false);
    setOwnerName(''); setOwnerEmail('');
    setSupportOwner(''); setTier('business'); setNotes('');
  }

  function slugify(s: string): string {
    return s.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
  }

  async function submit() {
    if (!canSubmit) return;
    try {
      const t = await create.mutateAsync({
        slug: slug.trim(),
        name: name.trim(),
        owner_name: ownerName.trim(),
        owner_email: ownerEmail.trim(),
        operator_owner: supportOwner.trim() || undefined,
        tier,
        notes: notes.trim() || undefined,
      });
      toast.success(`${t.name} onboarded`, 'Customer record created. Create their first instance next.', {
        to: `/tenants/${t.slug}`,
        label: 'Open',
      });
      reset();
      onClose();
      nav(`/tenants/${t.slug}`);
    } catch (e: any) {
      toast.danger('Could not onboard customer', e?.message ?? 'Unknown error');
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { if (!create.isPending) { reset(); onClose(); } }}
      size="md"
      title="Onboard a new customer"
      description="Creates the customer record only. Create instances from the customer's page after this."
      footer={
        <>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={create.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {create.isPending ? 'Creating…' : 'Onboard customer'}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Company name <span className="text-[color:var(--danger)]">*</span>
          </label>
          <input
            value={name}
            onChange={e => {
              const v = e.target.value;
              setName(v);
              // Keep slug in sync with name UNTIL the user manually edits the slug.
              if (!slugTouched) setSlug(slugify(v));
            }}
            placeholder="e.g. Acme Analytics"
            autoFocus
            disabled={create.isPending}
            className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
          />
        </div>

        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Slug <span className="text-[color:var(--danger)]">*</span>
          </label>
          <input
            value={slug}
            onChange={e => { setSlugTouched(true); setSlug(e.target.value.toLowerCase()); }}
            placeholder="e.g. acme"
            disabled={create.isPending}
            className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm font-mono"
          />
          <div className="mt-1 text-2xs text-fg-subtle">
            Becomes the URL: <code className="font-mono">{slug.trim() || '<slug>'}.nousviz.app</code>
          </div>
          {!validSlug && slug.length > 0 && (
            <div className="mt-1 text-2xs text-[color:var(--danger)] inline-flex items-center gap-1">
              <AlertCircle size={11} /> Lowercase letters, digits, dashes only
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
              Owner name <span className="text-[color:var(--danger)]">*</span>
            </label>
            <input
              value={ownerName}
              onChange={e => setOwnerName(e.target.value)}
              placeholder="e.g. Jane Doe"
              disabled={create.isPending}
              className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
            />
          </div>
          <div>
            <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
              Owner email <span className="text-[color:var(--danger)]">*</span>
            </label>
            <input
              value={ownerEmail}
              onChange={e => setOwnerEmail(e.target.value)}
              placeholder="jane@acme.com"
              type="email"
              disabled={create.isPending}
              className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm font-mono"
            />
          </div>
        </div>

        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Plan
          </label>
          <select
            value={tier}
            onChange={e => setTier(e.target.value)}
            disabled={create.isPending}
            className="w-full h-8 px-2 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
          >
            {(plans.data ?? [{ slug: 'business', name: 'Business' }]).map(p => (
              <option key={p.slug} value={p.slug}>{p.name}</option>
            ))}
          </select>
          <div className="mt-1 text-2xs text-fg-subtle">
            Drives instance limits and the licence from day one — changeable later in Settings.
          </div>
        </div>

        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Support owner <span className="text-fg-subtle font-normal lowercase tracking-normal">(operator on our side)</span>
          </label>
          <OperatorPicker value={supportOwner} onChange={setSupportOwner} disabled={create.isPending} />
        </div>

        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            placeholder="Internal context…"
            disabled={create.isPending}
            className="w-full px-3 py-2 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm leading-snug resize-none"
          />
        </div>
      </div>
    </Modal>
  );
}
